import { memo } from "react";
import type { CctvCamera } from "../map/layers";
import { useCvWall, type CvReading, type CvWallView } from "./useCvWall";

/**
 * CV VISION — the wall's live analysis.
 *
 * Reads as an instrument rather than a badge row: every number sits next to the
 * name of the algorithm that produced it and a plain statement of what that
 * algorithm cannot do. A camera with no reading says "no reading" — it never
 * shows 0, because a haze index of zero and a camera we have not looked at are
 * different facts and must not share a glyph.
 */

function Sparkline({ values, max }: { values: number[]; max: number }) {
  if (values.length < 2) return null;
  const w = 46;
  const h = 10;
  const step = w / (values.length - 1);
  const pts = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - Math.min(1, v / max) * h).toFixed(1)}`)
    .join(" ");
  return (
    <svg className="cv-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

/** Per-tile readout, rendered inside the tile it describes. */
export const CvTileOverlay = memo(function CvTileOverlay({ r }: { r?: CvReading }) {
  if (!r) return null;
  const hazeKnown = r.aod !== null;
  return (
    <span
      className="cv-overlay"
      data-severity={r.frameIssue === "no-signal" ? "dead" : hazeKnown ? r.hazeSeverity : "none"}
    >
      <span className="cv-line">
        <span className="cv-key">HAZE</span>
        <span className="cv-meter" aria-hidden>
          <i style={{ width: `${hazeKnown ? Math.min(100, r.aod! * 60) : 0}%` }} />
        </span>
        <span className="cv-val mono">{hazeKnown ? r.aod!.toFixed(2) : "—"}</span>
      </span>
      <span className="cv-line">
        <span className="cv-key">MOVE</span>
        <Sparkline values={r.motionSeries} max={4} />
        <span className="cv-val mono">{r.motion === null ? "—" : r.blobs}</span>
      </span>
    </span>
  );
});

interface HeaderProps {
  view: CvWallView;
}

/** Panel-level status: is it running, how much has it looked at, worst haze. */
export const CvVisionHeader = memo(function CvVisionHeader({ view }: HeaderProps) {
  if (view.analysed === 0) return null;
  return (
    <div className="cv-head">
      <span className={`cv-dot${view.running ? " on" : ""}`} aria-hidden />
      <strong>CV · VISION</strong>
      <span className="caption mono">
        {view.analysed} analysed · {view.tracked} cams
      </span>
      {view.haziest && (
        <span className="caption mono cv-worst">AOD {view.haziest.aod.toFixed(2)}</span>
      )}
    </div>
  );
});

export const CvVisionNote = memo(function CvVisionNote() {
  // One line in the rail; the method and its limits stay one hover away. The
  // full paragraph was five lines of methodology above an operator's camera grid.
  return (
    <p
      className="caption cv-note"
      title="Haze: dark-channel prior (He et al., CVPR 2009). Motion: frame differencing, 4-connected components. Computed in this browser from the live frame — nothing leaves this tab."
    >
      Haze and motion are image estimates — not PM2.5, not vehicle counts.
    </p>
  );
});

/** Convenience wrapper for tests and for stories. */
export function useWallView(cameras: CctvCamera[], apiBase: string): CvWallView {
  return useCvWall(cameras, apiBase);
}
